import React from 'react';
import {
  ReferenceArrayInput,
  BooleanInput,
  TextInput,
  SelectInput,
  required,
  FormDataConsumer,
  useTranslate,
  NumberInput,
} from 'react-admin';
import { Alert } from '@material-ui/lab';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from '@material-ui/core';
import {
  AutocompleteArrayTextInput,
  JsonInput,
  ReferenceInput,
  SelectArrayInput,
  CountryInput,
} from '~/components/ra';
import { Section } from '~/layout';
import { Longdash } from '~/utils';
import FXRatesSelect from './FXRates/FXRatesSelect';
import ProcessingFlowSelect from '~/components/ProcessingFlowSelect';
import OperationTypeInput from '~/components/ra/OperationTypeInput';
import {
  DEFAULT_ON_BEFORE_INIT_SCRIPT,
  DEFAULT_ON_BEFORE_CHECK_SCRIPT,
  DEFAULT_ON_INIT_SCRIPT,
  DEFAULT_ON_CHECK_SCRIPT,
} from '~/utils/constants/paymentServiceScripts';
import {
  InputHintButton,
  Drawer,
  CopyButton,
  TextInput as DisplayTextInput,
  GlTypeLabel,
} from '~/components';
import HelpIcon from '@material-ui/icons/Help';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import { PaymentService } from '~/types/paymentServices';

export const PAYMENT_SERVICES_OPERATIONS = [
  '', // any
  'BILL_PAYMENT',
  'INCOMING_TRANSFER',
  'OWN_ACCOUNTS_TRANSFER',
  'P2P_TRANSFER',
  'PAYOUT',
  'REMITTANCE',
  'REMITTANCE_PAYOUT',
  'REVERSAL',
  'TOP_UP',
  'VOUCHER_REDEMPTION',
] as const;

interface Props {
  edit?: boolean;
  record?: PaymentService;
}

const l = (key: string): string => `resources.paymentServices.${key}`;

const PaymentServiceForm = (props: Props) => {
  const { edit, record } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string, params?: Record<string, string>): string =>
    translate(l(`fields.${key}`), params);
  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);
  const handleButtonClick = () => {
    setOpen(true);
  };
  const [hintScript, setHintScript] = React.useState<string | null>(null);
  return (
    <>
      <FormDataConsumer>
        {() => {
          return (
            <Section>
              <TextInput source='id' label={t('id')} validate={required()} disabled={edit} />
              <TextInput source='name' label={t('name')} validate={required()} />
              <SelectInput
                source='status'
                choices={[
                  { id: 'ACTIVE', name: translate('components.ra.fields.StatusField.ACTIVE') },
                  { id: 'ARCHIVED', name: translate('components.ra.fields.StatusField.ARCHIVED') },
                ]}
                label={t('status')}
                validate={required()}
              />
              <TextInput source='templateId' label={t('templateId')} />
              <TextInput source='partnerId' label={t('partnerId')} />
              <ReferenceInput
                source='accountId'
                reference='tenant/accounts'
                filter={{
                  type: ['REGULAR'],
                  regulatoryType: ['EQUITY', 'POOL', 'SETTLEMENT'],
                }}
                allowEmpty
                emptyText={Longdash}
              >
                <SelectInput
                  optionText={(r: any) => (
                    <GlTypeLabel glType={r.glType} text={`${r.alias} (${r.number})`} />
                  )}
                />
              </ReferenceInput>
              <OperationTypeInput
                source='operationType'
                label={t('operationType')}
                style={{ marginBottom: '1.5em' }}
                record={record}
                optionsFilter={(o: any) =>
                  PAYMENT_SERVICES_OPERATIONS.includes(
                    ((o && (o.value ?? o.id)) || '') as typeof PAYMENT_SERVICES_OPERATIONS[number]
                  )
                }
              />
              <TextInput source='category' label={t('category')} />
              <TextInput source='description' label={t('description')} multiline />
              <TextInput source='instruction' label={t('instruction')} multiline />
              <NumberInput source='index' label={t('index')} />
              <TextInput source='region' label={t('region')} />
              <JsonInput
                label={t('parameters')}
                source='parameters'
                validationError={t('invalidJson')}
                initialValue='{}'
                required
              />
              <JsonInput
                label={t('parameterGroups')}
                source='parameterGroups'
                validationError={t('invalidJson')}
              />
              <FormDataConsumer>
                {({ formData }: { formData: any }) => {
                  const enabled = !!formData?.beneficiaryConfigurationEnabled;
                  const selection = formData?.beneficiary?.selection;
                  return (
                    <>
                      <BooleanInput
                        source='beneficiaryConfigurationEnabled'
                        label={t('beneficiaryConfigurationEnabled')}
                        defaultValue={!isEmpty(formData?.beneficiary)}
                      />
                      {enabled && (
                        <>
                          <div className={classes.root}>
                            <SelectInput
                              source='beneficiary.selection'
                              label={t('beneficiary.selection')}
                              choices={[
                                { id: 'DISABLED', name: t('beneficiary.DISABLED') },
                                { id: 'MANDATORY', name: t('beneficiary.MANDATORY') },
                                { id: 'OPTIONAL', name: t('beneficiary.OPTIONAL') },
                              ]}
                              defaultValue='DISABLED'
                              onChange={(e: any) => {
                                const newSelection = e.target.value;
                                if (newSelection !== 'DISABLED') {
                                  setOpen(true);
                                }
                              }}
                            />
                            <div className={classes.buttonContainer}>
                              <IconButton size='small' onClick={handleButtonClick}>
                                <HelpIcon fontSize='small' />
                              </IconButton>
                              <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
                                <DialogTitle>{t('beneficiary.selectionHintTitle')}</DialogTitle>
                                <DialogContent>
                                  <Typography style={{ whiteSpace: 'pre-wrap' }}>
                                    {t('beneficiary.selectionHint')}
                                  </Typography>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                          {selection !== 'DISABLED' && (
                            <>
                              <SelectInput
                                label={t('beneficiary.allowedTypes')}
                                source='beneficiary.allowedTypes'
                                choices={[
                                  { id: 'INDIVIDUAL', name: t('beneficiary.INDIVIDUAL') },
                                  { id: 'BUSINESS', name: t('beneficiary.BUSINESS') },
                                  { id: 'BOTH', name: t('beneficiary.BOTH') },
                                ]}
                                validate={required()}
                              />
                              <AutocompleteArrayTextInput
                                label={t('beneficiary.allowedPhoneNumberPrefixes')}
                                source='beneficiary.allowedPhoneNumberPrefixes'
                                record={record}
                                regularHelperText={t(
                                  'beneficiary.allowedPhoneNumberPrefixesHelperText'
                                )}
                              />
                              <CountryInput
                                label={t('beneficiary.allowedCountries')}
                                source='beneficiary.allowedCountries'
                                multiple
                                fullWidth
                              />
                            </>
                          )}
                        </>
                      )}
                    </>
                  );
                }}
              </FormDataConsumer>

              <FormDataConsumer>
                {({ formData }: { formData: any }) => {
                  const enabled = formData?.amountConfigurationEnabled;
                  return (
                    <>
                      <BooleanInput
                        source='amountConfigurationEnabled'
                        label={t('amountConfigurationEnabled')}
                        defaultValue={!isEmpty(formData?.amount)}
                      />
                      {enabled && (
                        <>
                          <SelectInput
                            source='amount.mode'
                            label={t('amountConfig.mode')}
                            choices={[
                              { id: 'DEFAULT', name: t('amountConfig.DEFAULT') },
                              { id: 'FIXED', name: t('amountConfig.FIXED') },
                              { id: 'SUGGESTED', name: t('amountConfig.SUGGESTED') },
                              {
                                id: 'BENEFICIARY_REMITTER',
                                name: t('amountConfig.BENEFICIARY_REMITTER'),
                              },
                            ]}
                            defaultValue='DEFAULT'
                            validate={required()}
                          />
                          {formData?.amount?.mode !== 'BENEFICIARY_REMITTER' && (
                            <JsonInput
                              source='amount.single'
                              label={t('amountConfig.singleAmountJson')}
                              validationError={t('invalidJson')}
                              required
                            />
                          )}
                          {formData?.amount?.mode === 'BENEFICIARY_REMITTER' && (
                            <>
                              <JsonInput
                                label={t('amountConfig.beneficiaryJson')}
                                source='amount.beneficiary'
                                validationError={t('invalidJson')}
                                required
                              />
                              <JsonInput
                                label={t('amountConfig.remitterJson')}
                                source='amount.remitter'
                                validationError={t('invalidJson')}
                                required
                              />
                            </>
                          )}
                        </>
                      )}
                    </>
                  );
                }}
              </FormDataConsumer>
              <FXRatesSelect />
              <JsonInput
                label={t('recipientExternalSource')}
                source='recipientExternalSource'
                validationError={t('invalidJson')}
              />
              <ProcessingFlowSelect />
              <JsonInput
                label={t('processingParameters')}
                source='processing.parameters'
                validationError={t('invalidJson')}
              />
              <JsonInput label={t('deal')} source='deal' validationError={t('invalidJson')} />
              <JsonInput label={t('i18n')} source='i18n' validationError={t('invalidJson')} />
              <AutocompleteArrayTextInput
                source='tags'
                label={t('tags')}
                record={record}
                regularHelperText={t('tagsHelperText')}
              />
              <JsonInput
                label={t('additionalProperties')}
                source='additionalProperties'
                validationError={t('invalidJson')}
              />
              <ReferenceArrayInput
                source='fallbackServiceIds'
                reference='paymentServices'
                label={t('fallbackServiceId')}
                perPage={100}
              >
                <SelectArrayInput optionText={(r: any) => (r ? `${r.id} (${r.name})` : '')} />
              </ReferenceArrayInput>
              <SelectInput
                source='approvalTransactionAuthorization'
                choices={[
                  {
                    id: 'REVIEW_INTERNALLY',
                    name: t('approvalTransactionAuthorization.REVIEW_INTERNALLY'),
                  },
                ]}
                allowEmpty
                label={t('approvalTransactionAuthorization._')}
              />
              <Alert severity='warning' style={{ marginBottom: 24 }}>
                {t('scriptsWarning', { field: t('onBeforeInit') })}
              </Alert>
              <InputHintButton
                text={t('viewOnBeforeInit')}
                onClick={() => setHintScript(DEFAULT_ON_BEFORE_INIT_SCRIPT)}
              >
                <TextInput label={t('onBeforeInit')} source='scripts.onBeforeInit' multiline />
              </InputHintButton>
              <Alert severity='warning' style={{ marginBottom: 24 }}>
                {t('scriptsWarning', { field: t('onInit') })}
              </Alert>
              <InputHintButton
                text={t('pasteOnInit')}
                onClick={() => setHintScript(DEFAULT_ON_INIT_SCRIPT)}
              >
                <TextInput
                  label={t('onInit')}
                  source='scripts.onInit'
                  multiline
                  style={{ width: '100%' }}
                />
              </InputHintButton>
              <Alert severity='warning' style={{ marginBottom: 24 }}>
                {t('scriptsWarning', { field: t('onBeforeCheck') })}
              </Alert>
              <InputHintButton
                text={t('viewOnBeforeCheck')}
                onClick={() => setHintScript(DEFAULT_ON_BEFORE_CHECK_SCRIPT)}
              >
                <TextInput label={t('onBeforeCheck')} source='scripts.onBeforeCheck' multiline />
              </InputHintButton>
              <Alert severity='warning' style={{ marginBottom: 24 }}>
                {t('scriptsWarning', { field: t('onCheck') })}
              </Alert>
              <InputHintButton
                text={t('pasteOnCheck')}
                onClick={() => setHintScript(DEFAULT_ON_CHECK_SCRIPT)}
              >
                <TextInput
                  label={t('onCheck')}
                  source='scripts.onCheck'
                  multiline
                  style={{ width: '100%' }}
                />
              </InputHintButton>
            </Section>
          );
        }}
      </FormDataConsumer>
      <Drawer
        heading={t('scriptExampleTitle')}
        open={hintScript !== null}
        onClose={() => setHintScript(null)}
      >
        {hintScript !== null && (
          <>
            <div className={classes.copyWrapper}>
              <span>{t('exampleScript')}</span>
              <CopyButton value={hintScript} classes={classes} />
            </div>

            <DisplayTextInput disabled multiline value={hintScript} fullWidth disableHelperText />
          </>
        )}
      </Drawer>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  buttonContainer: {
    position: 'absolute',
    top: 8,
    right: -40,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  copyWrapper: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  button: {
    padding: 0,
    color: theme.palette.grey[500],
    alignSelf: 'flex-start',
  },
  icon: {
    fontSize: '1rem',
  },
}));

export default PaymentServiceForm;
